import { useEffect, useState } from 'react';
import type { CSSProperties, FormEvent } from 'react';
import { Badge, Empty, Field, Note, Panel, Spinner } from '@wyocspay/ui';
import { ApiError, api, type MachineAccessState, type MachineResource } from './api.js';
import { CURRENCIES, currencyName } from '@wyocspay/shared/regions';

const buttonStyle: CSSProperties = {
  minHeight: 44,
  border: '1px solid var(--wc-blue-header)',
  borderRadius: 8,
  padding: '0 15px',
  background: 'var(--wc-blue-header)',
  color: '#fff',
  font: '600 13px var(--wc-font)',
  cursor: 'pointer',
};

function statusTone(status: string): 'ok' | 'warn' | 'pending' | 'fail' {
  if (status === 'APPROVED') return 'ok';
  if (status === 'REJECTED' || status === 'REVOKED') return 'fail';
  if (status === 'PENDING') return 'pending';
  return 'warn';
}

export function MachinePaymentsView() {
  const [access, setAccess] = useState<MachineAccessState | null>(null);
  const [resources, setResources] = useState<MachineResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [form, setForm] = useState({ slug: '', name: '', upstream_url: '', protocol: 'both', price_minor: '100', currency: 'usd', livemode: false });

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [nextAccess, nextResources] = await Promise.all([api.machinePayments(), api.machineResources()]);
      setAccess(nextAccess);
      setResources(nextResources.data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  async function requestAccess() {
    setBusy(true); setError(null); setNotice(null);
    try { setAccess(await api.requestMachinePayments()); setNotice('Your request was sent. It will appear here as pending until WyoCSPay reviews it.'); }
    catch (err) { setError(err instanceof ApiError ? err.message : String(err)); }
    finally { setBusy(false); }
  }

  async function createResource(event: FormEvent) {
    event.preventDefault();
    setBusy(true); setError(null); setNotice(null);
    try {
      const created = await api.createMachineResource({
        slug: form.slug, name: form.name, upstream_url: form.upstream_url || null,
        protocol: form.protocol as 'x402' | 'mpp' | 'both', price_minor: Number(form.price_minor), currency: form.currency,
        livemode: form.livemode, status: 'ACTIVE',
      });
      setResources((rows) => [created, ...rows]);
      setNotice('Resource created. Copy its URL into your website or agent integration.');
      setForm((f) => ({ ...f, slug: '', name: '', upstream_url: '' }));
    } catch (err) { setError(err instanceof ApiError ? err.message : String(err)); }
    finally { setBusy(false); }
  }

  async function provisionAddress() {
    setBusy(true); setError(null); setNotice(null);
    try {
      const result = await api.provisionMachineDepositAddress({ network: 'tempo', livemode: true });
      setNotice(`Stripe created a deposit address for this Connect account: ${result.address}`);
    } catch (err) { setError(err instanceof ApiError ? err.message : String(err)); }
    finally { setBusy(false); }
  }

  if (loading) return <Spinner label="Loading machine payments…" />;
  if (error && !access) return <Note tone="danger">{error}</Note>;
  if (!access) return null;
  const liveMppReady = access.platform.enabled && access.platform.mpp_enabled && access.platform.mpp_live_approved;
  const x402CurrencyConflict = (form.protocol === 'x402' || form.protocol === 'both') && form.currency !== 'usd';

  return (
    <div style={{ display: 'grid', gap: 18, maxWidth: 1080 }}>
      <Panel title="Machine payments">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div>
            <p style={{ margin: '0 0 8px', lineHeight: 1.55, color: 'var(--wc-body)' }}>
              Publish a paid API, dataset, or agent tool. Each resource uses this merchant’s Stripe Connect account; WyoCSPay never uses a shared payment address for your customers.
            </p>
            <p style={{ margin: 0, color: 'var(--wc-muted)', fontSize: 13 }}>
              Access is enabled after WyoCSPay reviews your Stripe account and settlement settings.
            </p>
          </div>
          <Badge tone={statusTone(access.status)}>{access.status.replace('_', ' ')}</Badge>
        </div>
        <div style={{ marginTop: 18, padding: 16, border: '1px solid var(--wc-border)', borderRadius: 10, background: 'var(--wc-soft)' }}>
          <strong style={{ display: 'block', marginBottom: 10, color: 'var(--wc-heading)' }}>How it works</strong>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, color: 'var(--wc-body)', fontSize: 13, lineHeight: 1.45 }}>
            <div><strong>1. Choose a protocol</strong><br />MPP uses Stripe. x402 uses USDC through your facilitator.</div>
            <div><strong>2. Describe your resource</strong><br />Add the HTTPS endpoint, price, currency, and a short name.</div>
            <div><strong>3. Publish after approval</strong><br />Copy the generated resource URL into your site or agent.</div>
          </div>
        </div>
        {access.status !== 'APPROVED' ? (
          <div style={{ marginTop: 18 }}>
            {access.rejection_reason ? <Note tone="danger" title="Access request not approved">Reason: {access.rejection_reason}</Note> : null}
            {access.status === 'NOT_REQUESTED' || access.status === 'REJECTED' || access.status === 'REVOKED' ? (
              <button type="button" style={buttonStyle} disabled={busy} onClick={() => void requestAccess()}>
                {busy ? 'Sending…' : 'Request access'}
              </button>
            ) : <Note tone="info" title="Review in progress">Your request is with the WyoCSPay team. A live payment resource cannot be created before approval.</Note>}
          </div>
        ) : (
          <div style={{ marginTop: 18, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Badge tone={access.protocols.mpp ? 'ok' : 'neutral'}>MPP {access.protocols.mpp ? 'approved' : 'off'}</Badge>
            <Badge tone={access.protocols.x402 ? 'ok' : 'neutral'}>x402 {access.protocols.x402 ? 'approved' : 'off'}</Badge>
            {access.protocols.mpp ? <button type="button" style={{ ...buttonStyle, background: 'transparent', color: 'var(--wc-blue-header)', opacity: liveMppReady ? 1 : 0.55 }} disabled={busy || !liveMppReady} onClick={() => void provisionAddress()}>Create Stripe deposit address</button> : null}
          </div>
        )}
        {access.approved && access.protocols.mpp && !liveMppReady ? <Note tone="info" title="MPP is approved, but live access is still pending">The platform Stripe/live gate is still in preview. No deposit address is created and no live funds are accepted until all operator checks are enabled.</Note> : null}
      </Panel>

      {notice ? <Note tone="ok">{notice}</Note> : null}
      {error ? <Note tone="danger">{error}</Note> : null}

      {access.approved ? <>
        <Panel title="Create a payment resource">
          <form onSubmit={(event) => void createResource(event)} style={{ display: 'grid', gap: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14 }}>
              <Field label="Resource slug" hint="A short URL-safe ID. Example: company-search."><input className="wc-input" required value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="company-search" /></Field>
              <Field label="Resource name" hint="A clear name your team and customers will recognize."><input className="wc-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Company search API" /></Field>
              <Field label="Upstream HTTPS URL" hint="The protected endpoint that returns your data. WyoCSPay calls it only after payment verification."><input className="wc-input" type="url" required value={form.upstream_url} onChange={(e) => setForm({ ...form, upstream_url: e.target.value })} placeholder="https://your-api.example/data" /></Field>
              <Field label="Price (minor units)" hint="Enter the smallest currency unit: 100 = $1.00 or €1.00. There is no decimal point."><input className="wc-input" type="number" min="1" required value={form.price_minor} onChange={(e) => setForm({ ...form, price_minor: e.target.value })} /></Field>
              <Field label="Payment protocol" hint="MPP is Stripe-based. x402 is USDC-based and requires an approved facilitator and wallet.">
                <select className="wc-input" value={form.protocol} onChange={(e) => setForm({ ...form, protocol: e.target.value })}><option value="both">MPP + x402</option><option value="mpp">MPP / Stripe</option><option value="x402">x402 / facilitator</option></select>
              </Field>
              <Field label="Currency" hint="The currency used for the resource price and Stripe payment. Use the currency supported by your Connect account, such as EUR for a European merchant.">
                <select className="wc-input" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}>
                  {CURRENCIES.map((currency) => <option key={currency.code} value={currency.code}>{currency.symbol} {currencyName(currency.code, 'en')} ({currency.code.toUpperCase()})</option>)}
                </select>
              </Field>
            </div>
            {x402CurrencyConflict ? <Note tone="warn" title="x402 resources use USDC">Choose USD for x402. The current facilitator expects USDC amounts in dollar units. For EUR or another fiat currency, choose MPP / Stripe only.</Note> : null}
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}><input type="checkbox" checked={form.livemode} onChange={(e) => setForm({ ...form, livemode: e.target.checked })} /> Publish as live (only after the approved live gates are enabled)</label>
            <button type="submit" style={buttonStyle} disabled={busy || x402CurrencyConflict}>{busy ? 'Creating…' : 'Create resource'}</button>
          </form>
        </Panel>

        <Panel title="Published links" padded={false}>
          <div style={{ padding: '14px 16px 0', color: 'var(--wc-muted)', fontSize: 13 }}>Use the payment URL in your website or agent integration. Human checkout links continue to use <strong>pay.wyocs.org</strong>.</div>
          {resources.length === 0 ? <Empty>No resources yet. Create one above.</Empty> : <div style={{ overflowX: 'auto' }}><table className="wc-table"><thead><tr><th>Name</th><th>Protocol</th><th>Mode</th><th>Resource URL</th><th>Discovery</th></tr></thead><tbody>{resources.map((row) => <tr key={row.id}><td><strong>{row.name}</strong><br /><span style={{ color: 'var(--wc-muted)', fontSize: 12 }}>{row.slug}</span></td><td>{row.protocol}</td><td>{row.livemode ? 'LIVE' : 'TEST'}</td><td><a href={row.resource_url} target="_blank" rel="noreferrer">{row.resource_url}</a></td><td>{row.mpp_discovery_url ? <a href={row.mpp_discovery_url} target="_blank" rel="noreferrer">openapi.json</a> : '—'}</td></tr>)}</tbody></table></div>}
        </Panel>
      </> : null}
    </div>
  );
}
